import { forwardRef, type TextareaHTMLAttributes, type ReactNode } from 'react';

export interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'children'> {
  id: string;
  label: ReactNode;
  helpText?: ReactNode;
  errorText?: ReactNode;
  /** Visually hide the label (kept for screen readers). Mirrors TextField. */
  hideLabel?: boolean;
}

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  function TextAreaField(
    { id, label, helpText, errorText, className, rows = 3, hideLabel, ...rest },
    ref
  ) {
    const helpId = helpText ? `${id}-help` : undefined;
    const errorId = errorText ? `${id}-error` : undefined;
    const describedBy =
      [helpId, errorId].filter(Boolean).join(' ') || undefined;
    return (
      <div className={['pn-field', className].filter(Boolean).join(' ')}>
        <label
          htmlFor={id}
          className={['pn-field-label', hideLabel ? 'pn-field-label-hidden' : null]
            .filter(Boolean)
            .join(' ')}
        >
          {label}
        </label>
        <textarea
          ref={ref}
          id={id}
          rows={rows}
          className="pn-field-input pn-field-textarea"
          aria-describedby={describedBy}
          aria-invalid={errorText ? true : undefined}
          {...rest}
        />
        {helpText ? (
          <span id={helpId} className="pn-field-help">
            {helpText}
          </span>
        ) : null}
        {errorText ? (
          <span id={errorId} className="pn-field-error" role="alert">
            {errorText}
          </span>
        ) : null}
      </div>
    );
  }
);
